// @givmo/app-charity — the ONE "Continue" into an organization the caller has just come to hold
// (CPA-WEB83).
//
// Two screens end a claim with Continue: the claim wizard's AUTO_VERIFIED panel and the claim page's
// approved card (a Tier-2 claim a reviewer approved). Both can paint while the memberships read still
// holds the caller's PRE-approval list — the wizard because the panel paints from the cache before the
// submit's awaited refresh lands (CPA-WEB51's ordering, kept), the claim page because the approval
// arrives on its CLAIMS read (the poll or Refresh) and nothing there re-asks memberships. A Continue
// that went to the root on that stale list met the portal-entry gate's `loaded-empty` answer: the
// "no organization yet" caller was sent to the org search, and when the fresh read landed the
// console shell rendered there, on a claim-first path that never moves a member. The new Owner was
// left on "Find your organization" (the onboarding rehearsal's red; a slow network did the same).
//
// What both screens do now, in order:
//   1. HOLD (`pending` → the Button's `loading`) while the memberships read is in flight without this
//      charity. A watcher, never an asker (`enabled: false`), spread from the ONE query definition as
//      every observer of it must be (`membershipsQueryOptions`'s ⚠).
//   2. With `refresh`, ASK for that read once, when the list does not hold the charity yet — the
//      claim page's case, where nothing else is asking. Held from the first paint until the ask has
//      gone out, so no click lands in the frame before the effect. The wizard passes no `refresh`:
//      its submit's invalidation is the ask.
//   3. Once the list holds the charity, OPEN the organization deep link (CPA-BE29's door,
//      `router/organization-deep-link.tsx`): it selects THIS organization through the one writer and
//      lands on its console's landing — the right console for an Owner who administers others too.
//      A read that settles without the charity (an error) releases Continue to the doorway, as
//      before, whose own error state is the way back.
//
// Pinned through the real route tree: the wizard in `router/claim-wizard-terminal.test.tsx`, the
// claim page in `router/claim-first-gate.test.tsx`.

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { organizationDeepLinkPath } from '../console/console-routes';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../features/auth/auth-routes';
import { membershipsQueryOptions } from './active-org-context';

export interface ContinueIntoOrganization {
  /** True while Continue must wait for the memberships read (render it as the Button's `loading`). */
  pending: boolean;
  /** Into the organization's console once the list holds it; to the doorway otherwise. */
  onContinue: () => void;
}

/**
 * Continue into `charityId` once the memberships read holds it (see the header). `null` while the
 * caller does not know the charity yet: nothing is held or asked, and Continue goes to the doorway.
 */
export function useContinueIntoOrganization(
  charityId: string | null,
  { refresh = false }: { refresh?: boolean } = {},
): ContinueIntoOrganization {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membershipsRead = useQuery({
    ...membershipsQueryOptions(),
    enabled: false,
    notifyOnChangeProps: ['data', 'isFetching'],
  });
  const holds =
    charityId !== null && (membershipsRead.data ?? []).some((m) => m.charity_id === charityId);

  const wantsAsk = refresh && charityId !== null && !holds;
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (!wantsAsk || asked) return;
    setAsked(true);
    void queryClient.invalidateQueries({ queryKey: membershipsQueryOptions().queryKey });
  }, [asked, queryClient, wantsAsk]);

  return {
    pending: !holds && (membershipsRead.isFetching || (wantsAsk && !asked)),
    onContinue: () => {
      if (holds && charityId !== null) void navigate({ href: organizationDeepLinkPath(charityId) });
      else void navigate({ to: DEFAULT_AUTHENTICATED_ROUTE });
    },
  };
}
